import React, { Fragment, ReactNode } from 'react';
import {
    Grid,
    Skeleton,
    styled,
    Table,
    TableBody,
    TableRow,
    TableCell,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';

import { GraphDataPoint } from '../../interfaces/graphInterfaces';
import { ValueReferences } from '../../utils/enums';
import { formatCurrency, formatNumber } from '../../utils/formattingUtils';
import TransitLegend from './TransitLegend';
import BoxGraph from '../Graphs/BoxGraph';

const classesPrefix = 'transit';

const classes = {
    root: `${classesPrefix}-root`,
    legendWrapper: `${classesPrefix}-legendWrapper`,
    legendItemWrapper: `${classesPrefix}-legendItemWrapper`,
    table: `${classesPrefix}-table`,
    rightTableCol: `${classesPrefix}-rightTableCol`,
    emptyMiles: `${classesPrefix}-emptyMiles`,
    graphSectionTooltip: `${classesPrefix}-graphSectionTooltip`,
    subBoxGraph: `${classesPrefix}-subBoxGraph`,
};

const StyledDiv = styled('div')(({ theme }) => {
    return {
        [`&.${classes.root}`]: {
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
        },
        [`& .${classes.legendWrapper}`]: {
            marginTop: 0,
        },
        [`& .${classes.legendItemWrapper}`]: {
            maxWidth: '500px',
        },
        [`& .${classes.table}`]: {
            '& td': {
                borderBottom: 'none',
                paddingLeft: 0,
                paddingRight: 0,
                paddingBottom: 0,
            },
        },
        [`& .${classes.rightTableCol}`]: {
            textAlign: 'right',
            paddingLeft: `${theme.spacing(3)} !important`,
        },
        [`& .${classes.emptyMiles}`]: {
            marginLeft: theme.spacing(1),
            color: theme.palette.text.secondary,
            fontSize: theme.typography.body2.fontSize,
        },
        [theme.breakpoints.down('lg')]: {
            [`& .${classes.legendItemWrapper}`]: {
                maxWidth: 'none',
            },
        },
    };
});

const StyledGrid = styled(Grid)(({ theme }) => {
    return {
        [`&.${classes.graphSectionTooltip}`]: {
            width: '100%',
            padding: theme.spacing(1),
        },
        [`& .${classes.subBoxGraph}`]: {
            display: 'flex',
            minHeight: theme.spacing(3),
        },
    };
});

const Transit = ({
    loading,
    orderVolume,
    transitHours,
    dwellTime,
    inTransitDelayHours,
    emptyMiles,
    loadedMiles,
    totalRevenue,
    totalMiles,
    loadTime,
    unloadTime,
    emptyHours,
}: {
    loading: boolean;
    orderVolume: number | null;
    /** departure from first stop to arrival at final stop */
    transitHours: number | null;
    dwellTime: number | null;
    /** transit - standardTransit */
    inTransitDelayHours: number | null;
    emptyMiles: number | null;
    loadedMiles: number | null;
    totalRevenue: number | null;
    totalMiles: number | null;
    loadTime: number | null;
    unloadTime: number | null;
    emptyHours: number | null;
}): React.JSX.Element => {
    const theme = useTheme();
    const standardTransitHours = transitHours !== null && inTransitDelayHours !== null ? transitHours - inTransitDelayHours : null; // standard transit + in transit delay = transit

    const colors = theme.palette.dataVisualization[6];
    const standardTransitColor = colors[6];
    const inTransitDelayColor = colors[5];

    const transitTooltip = (
        <StyledGrid
            container
            direction='column'
            alignItems='stretch'
            className={classes.graphSectionTooltip}
        >
            <Grid item xs className={classes.subBoxGraph}>
                <div
                    style={{
                        backgroundColor: standardTransitColor,
                        ...transitHours !== null && standardTransitHours !== null && {
                            flexGrow: inTransitDelayHours !== null && inTransitDelayHours < 0 ?
                                transitHours / standardTransitHours :
                                standardTransitHours / transitHours,
                        },
                    }}
                />
                <div
                    style={{
                        background: inTransitDelayHours !== null && inTransitDelayHours < 0 ?
                            `repeating-linear-gradient(-45deg, ${inTransitDelayColor}, ${inTransitDelayColor} 8px, ${standardTransitColor} 8px, ${standardTransitColor} 16px)` :
                            inTransitDelayColor,
                        ...transitHours !== null && inTransitDelayHours !== null && {
                            flexGrow: standardTransitHours && inTransitDelayHours < 0 ?
                                Math.abs(inTransitDelayHours / standardTransitHours) :
                                inTransitDelayHours / transitHours,
                        },
                    }}
                />
            </Grid>
            <Grid item xs>
                <TransitLegend
                    data={[
                        {
                            name: 'Standard Transit',
                            value: orderVolume && standardTransitHours !== null ?
                                standardTransitHours / orderVolume :
                                null,
                        },
                        {
                            name: 'In Transit Delay',
                            value: orderVolume && inTransitDelayHours !== null ?
                                inTransitDelayHours / orderVolume :
                                null,
                        },
                    ]}
                    colors={[
                        standardTransitColor,
                        inTransitDelayColor,
                    ]}
                    loading={loading}
                />
            </Grid>
        </StyledGrid>
    );

    const hourlyData = [
        {
            name: 'Empty Hours',
            value: orderVolume && emptyHours !== null
                ? emptyHours / orderVolume
                : null,
        },
        {
            name: 'Dwell',
            value: orderVolume && dwellTime !== null
                ? dwellTime / orderVolume
                : null,
        },
        {
            name: 'Loading',
            value: orderVolume && loadTime !== null
                ? loadTime / orderVolume
                : null,
        },
        {
            name: 'Transit',
            value: orderVolume && transitHours !== null
                ? transitHours / orderVolume
                : null,
            tooltipContents: transitTooltip,
        },
        {
            name: 'Unloading',
            value: orderVolume && unloadTime !== null
                ? unloadTime / orderVolume
                : null,
        },
    ];

    const graphData = hourlyData.map((data): GraphDataPoint => {
        return {
            ...data,
            value: data.value ?? 0,
        };
    });

    const totalTimePerOrder = graphData.reduce((prev, { value }) => {
        return prev + (value ?? 0);
    }, 0);

    const renderHoursPerOrder = (): ReactNode => {
        if (loading) {
            return <Skeleton width={175} />;
        }

        if (!orderVolume) {
            return ValueReferences.NA;
        }

        return `${formatNumber(totalTimePerOrder, 2, 2)} Hours/Order`;
    };

    return (
        <StyledDiv className={classes.root}>
            <Grid item xs='auto'>
                <Typography variant='h4'>Transit</Typography>
            </Grid>
            <Grid item xs='auto'>
                <Grid
                    container
                    direction='row'
                    sx={{
                        justifyContent: 'flex-end',
                    }}
                >
                    <Grid item xs>
                        <Typography variant='h6'>
                            {
                                loading ? (
                                    <Skeleton width={300} />
                                ) : (
                                    <Fragment>
                                        <Tooltip title='Avg. Length of Haul (per order)'>
                                            <span>
                                                {
                                                    loadedMiles !== null && orderVolume ?
                                                        `${formatNumber(loadedMiles / orderVolume, 0, 0)} LOH` :
                                                        'N/A LOH'
                                                }
                                            </span>
                                        </Tooltip>
                                        <span className={classes.emptyMiles}>
                                            {
                                                emptyMiles !== null && orderVolume ?
                                                    `${formatNumber(emptyMiles / orderVolume, 0, 0)} Empty Miles (${totalMiles ?
                                                        formatNumber((emptyMiles / totalMiles) * 100, 0, 0) :
                                                        null
                                                    }%)` :
                                                    'N/A Empty Miles'
                                            }
                                        </span>
                                    </Fragment>
                                )
                            }
                        </Typography>
                    </Grid>
                    <Grid item xs='auto'>
                        <Typography variant='h6'>
                            {renderHoursPerOrder()}
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs='auto'>
                <BoxGraph
                    loading={loading}
                    data={loading || orderVolume === 0 ? [] : graphData}
                />
            </Grid>
            <Grid item xs={12}>
                <Grid
                    container
                    spacing={2}
                    className={classes.legendWrapper}
                    sx={{
                        justifyContent: 'space-between',
                    }}
                >
                    <Grid item xs={12} sm={6} md={12} lg={6} className={classes.legendItemWrapper} style={{ paddingTop: 0 }}>
                        <TransitLegend
                            loading={loading}
                            data={hourlyData}
                            colors={colors}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={12} lg={6} className={classes.legendItemWrapper} style={{ paddingTop: 0 }}>
                        <Table size='small' className={classes.table}>
                            <TableBody>
                                <TableRow>
                                    <TableCell>MPH</TableCell>
                                    <TableCell className={classes.rightTableCol}>
                                        {
                                            loading ? (
                                                <Skeleton />
                                            ) : (
                                                <strong>
                                                    {formatNumber(loadedMiles !== null && transitHours ? loadedMiles / transitHours : null, 0, 0)}
                                                </strong>
                                            )
                                        }
                                    </TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell>RPH</TableCell>
                                    <TableCell className={classes.rightTableCol}>
                                        {
                                            loading ? (
                                                <Skeleton width={150} />
                                            ) : (
                                                <strong>
                                                    {formatCurrency(totalRevenue !== null && transitHours ? totalRevenue / transitHours : null)}
                                                </strong>
                                            )
                                        }
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </Grid>
                </Grid>
            </Grid>
        </StyledDiv>
    );
};

export default Transit;
